/**
 * Today (UX_SCOPE, ROLE_ONTOLOGY moment map). One primary thing per role per moment:
 *  - manager: opening → "Open for food?"; day → only what needs doing now; close → the still-open handoff; evening → one line.
 *  - staff: their gate (check in), then the next due check, then the three verbs.
 * Live: refreshes on synced changes, or every 10 s when live sync isn't available (then shows "Updated … ago").
 */
import { roomCheckPath } from "../lib/roomCheck.ts";
import { Suspense, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { returnRulesFor, checkinActionsFor, placeState, DAY_NAMES, HEALTH_DECISION_BLOCK_TITLE, checkinAction, firstName, greetingFor, workerGateLine, isLate, nextOpening, type Employee, type Finding, type Location } from "@logwave/core";
import type { TodayResponse } from "../api.ts";
import { CheckRow, type Expected } from "../components/Checks.tsx";
import { FindingCard } from "../components/Findings.tsx";
const Routine = lazyInline(() => import("../components/Routine.tsx"), "Routine");
import { RoomsLine } from "../components/RoomBoard.tsx";
import { HealthLine } from "../components/Health.tsx";
const CleanPlaceLine = lazyInline(() => import("./CleanPlace.tsx"), "CleanPlaceLine");
import { FirstDayWins } from "../components/QuickWins.tsx";
import { Icon } from "../components/Icons.tsx";
import { SupplyListCard } from "../components/SupplyList.tsx";
import { HandoffShift, PutInCharge } from "../components/Handoff.tsx";
import { NeedsHelp } from "../components/NeedsHelp.tsx";
const SignsNudge = lazyInline(() => import("./Signs.tsx"), "SignsNudge");
import { AnchorButton, Blocked, Card, Empty, ErrorBox, LinkButton, Notice, Page, Pill, Section, Skeleton, Progress } from "../components/ui.tsx";
import { useLiveRefresh, useNow } from "../lib/useLive.ts";
import { useLoad } from "../lib/useLoad.ts";
import { usePrefetchTabs, lazyInline } from "../lib/lazy.ts";
import { LastInspectionLazy } from "./lastInspectionLazy.ts";
import { MOMENTS, momentNow, type Moment } from "../lib/moment.ts";
import { SEVERITY_GROUP, caughtEarly, controlledShifts, findingTitle, shiftStatus, sickWorkerGuide } from "../lib/meaning.ts";
import { addDays, isoToLocalTime, localDate, localTime, prettyTime } from "../lib/time.ts";
import { FeatureTrialCards } from "../components/FeatureTrials.tsx";
import { updatedLabel, type SyncStatus } from "../sync.ts";
import { useApi, useSession } from "../session.tsx";

/** The signs nudge, from the Signs screen's own chunk: a static import kept the whole Signs screen in the main chunk. */

const TABLES = ["log_entries", "daily_checkins", "incidents", "review_items", "employees", "tasks", "locations"] as const;

/** One source with the moment (core greetingFor). */
const greeting = (moment: Moment, hhmm: string) => greetingFor(moment, Number(hhmm.slice(0, 2)));

export function Today() {
  const api = useApi();
  const s = useSession();
  const [params] = useSearchParams();
  const isManager = s.can("manager");
  const [version, setVersion] = useState(0);
  // s.location is a dependency so a logging-level change in Settings updates the checks at once.
  const today = useLoad(() => api.today(s.org.id, s.location.id), [api, s.org.id, s.location]);
  const sync = useLiveRefresh(async () => { await today.reload(); setVersion((v) => v + 1); }, TABLES);
  const tz = s.location.timezone;
  const nowHHMM = localTime(new Date(), tz);
  const wanted = params.get("moment") as Moment | null;
  // Weekly hours (core hours.ts): today's own hours; closed today reads as evening.
  const moment: Moment = wanted && MOMENTS.includes(wanted) ? wanted : momentNow(s.location);
  const t = today.data;
  usePrefetchTabs(!!t);

  const dateLine = t ? new Date(t.date + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" }) : "";
  const kicker = <>{dateLine || "Today"} · {s.location.name}</>;

  if (!t) {
    return (
      <Page title="Today's full list" kicker={kicker}>
        {today.error ? <ErrorBox error={`We couldn't load today. ${today.error}`} onRetry={today.reload} /> : <Skeleton rows={4} label="Loading today" />}
      </Page>
    );
  }
  // A nag's link (/today?report=ID): that report first, on its own.
  const reportId = params.get("report");
  if (reportId) {
    const f = t.findings.find((x) => x.kind === "incident" && x.ref === reportId);
    return (
      <Page title="The report you were sent" kicker={kicker} narrow>
        <div className="stack">
          {f ? <ul className="findings"><FindingCard f={f} /></ul> : <Notice tone="go" title="Already handled">That report is closed. Thank you.</Notice>}
          <LinkButton to="/today" variant="secondary" icon="chevron">Back to today's full list</LinkButton>
        </div>
      </Page>
    );
  }
  return isManager
    ? <ManagerToday t={t} moment={moment} sync={sync} kicker={kicker} version={version} reload={today.reload} />
    : <StaffToday t={t} moment={moment} sync={sync} nowHHMM={nowHHMM} version={version} />;
}

/**
 * A room Clear ✓ today whose cleaning log isn't logged yet (core unloggedClears; "Skip the log for now" on the Clear
 * screen): one row each, opening the room check's Clear screen where "Log it" is the primary.
 */
function RoomLogs({ t }: { t: TodayResponse }) {
  const rows = t.roomLogs ?? [];
  if (!rows.length) return null;
  return (
    <Section kicker="To log" title="Cleaning logs">
      <ul className="stack-sm" aria-label="Cleaning logs not logged">
        {rows.map((r) => (
          <li key={r.runId} className="card card-flat row-nowrap">
            <Icon name="write" size={20} /><span className="grow">{r.line}</span>
            <LinkButton to={roomCheckPath(r.room)} variant="secondary" small icon="chevron" iconEnd>Log this cleaning</LinkButton>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** "Add the photo for <check>" (core proof.ts P5): how many wait (mine, or everyone's for a manager); Checks lists them, one tap each. */
function PhotosToAdd({ t }: { t: TodayResponse }) {
  const n = t.proofGaps?.length ?? 0;
  return n ? <LinkButton to="/do" variant="secondary" small icon="camera" data-testid="photos-to-add">{`Photos still to add (${n})`}</LinkButton> : null;
}

export function SyncNote({ sync }: { sync: SyncStatus }) {
  useNow(15_000);
  if (sync.mode === "live") return <span className="stale stale-live" title="Updates as they happen"><span className="dot" />Live</span>;
  return <span className="stale" title={sync.reason}><span className="dot" />{updatedLabel(sync.lastUpdated)}</span>;
}

/* ─────────────── manager ─────────────── */

function ManagerToday({ t, moment, sync, kicker, version, reload }: { t: TodayResponse; moment: Moment; sync: SyncStatus; kicker: React.ReactNode; version: number; reload: () => Promise<void> }) {
  const s = useSession();
  const tz = s.location.timezone;
  const onShift = t.onShift.map((o) => o.employee).filter(Boolean) as Employee[];
  const groups = (["urgent", "high", "normal"] as const).map((sev) => ({ sev, items: t.findings.filter((f) => f.severity === sev) })).filter((g) => g.items.length);
  const doneCount = t.expected.filter((e) => e.satisfied).length;
  const nextWash = t.nudges.handwash.find((h) => h > localTime(new Date(), tz));

  const band = <OpenForFood t={t} primary={moment === "before" || moment === "opening"} onChange={reload} />;
  const later = groups.filter((g) => g.sev !== "urgent");
  const urgent = groups.find((g) => g.sev === "urgent");

  const fix = (
    <Section title="To fix" kicker={t.findings.length ? `${t.findings.length} open` : undefined}>
      {groups.length === 0 ? <Empty>Nothing to fix. The cleaver approves.</Empty> : moment === "day" ? (
        <>
          {/* During service: only what needs doing now; everything else waits one tap away. */}
          {urgent ? <FindingGroup sev="urgent" items={urgent.items} onShift={onShift} /> : <p className="muted">Nothing needs you right now.</p>}
          {later.length > 0 && (
            <details className="card card-flat">
              <summary className="disclose">When you have a minute ({later.reduce((n, g) => n + g.items.length, 0)})</summary>
              <div style={{ marginTop: 12 }}>{later.map((g) => <FindingGroup key={g.sev} sev={g.sev} items={g.items} onShift={onShift} />)}</div>
            </details>
          )}
        </>
      ) : groups.map((g) => <FindingGroup key={g.sev} sev={g.sev} items={g.items} onShift={onShift} />)}
    </Section>
  );

  return (
    <Page title="Today" kicker={kicker} actions={<SyncNote sync={sync} />}>
      <div className="today">
        <div className="stack">
          {/* A teammate asked for help on a step (core helprequest.ts): first, until someone is on the way. */}
          <NeedsHelp version={version} />
          {moment === "evening" && <EveningLine t={t} version={version} />}
          {moment === "close" ? (
            <>
              <Handoff t={t} onShift={onShift} reload={reload} />
              {!t.openForFood.ready && band}
            </>
          ) : (moment !== "evening" || !t.openForFood.ready) && band}
          {/* First day (core firstDay): the two quick wins, label food and inspection supplies (screens/LabelFood, ShopKit). */}
          {moment !== "evening" && t.expected.some((e) => e.firstDay) && <FirstDayWins manager />}
          {/* The mixed routine (docs/ACTIVITIES.md): below the open-for-food band, above what's left to fix. */}
          {/* The thank-you trial of an add-on (docs/FEATURE-TRIALS.md): owners and admins only; one line during service. Below the food-safety band, never above it. */}
          <FeatureTrialCards compact={moment === "day"} />
          {moment !== "evening" && <RoomsLine version={version} />}
          {/* Location health (docs/LOCATION_HEALTH.md): every area in one line, for managers. */}
          <HealthLine version={version} />
          {/* Your last inspection (core lastinspection.ts): one quiet row while a cited item isn't a daily check yet. */}
          {moment !== "evening" && !!s.location.officialPlaceKey && <Suspense fallback={null}><LastInspectionLazy row /></Suspense>}
          {moment !== "evening" && <CleanPlaceLine />}
          <RoomLogs t={t} />
          {/* "Add the photo for <check>" (core proof.ts P5): everyone's from the last week, for a manager. */}
          <PhotosToAdd t={t} />
          {moment !== "evening" && <Routine expected={t.expected} version={version} title="Today's routine" />}
          {moment === "evening" ? (t.findings.length > 0 && <details className="card card-flat"><summary className="disclose">The full list ({t.findings.length})</summary><div style={{ marginTop: 12 }}>{fix}</div></details>) : moment !== "close" && fix}
          {(t.integrity?.length ?? 0) > 0 && (
            <Section kicker="Worth a look">
              <Card flat className="stack-sm">
                {t.integrity!.map((f, i) => <p key={i} className="row-nowrap small"><Icon name="eye" size={18} /><span className="grow">{f.message}</span></p>)}
                <p className="caption">A question, not an accusation. Real readings wobble a little; the same number every day is worth checking.</p>
              </Card>
            </Section>
          )}
          <OnShift t={t} onChange={reload} />
          <NorthStar t={t} version={version} doneCount={doneCount} />
        </div>
        <aside className="today-rail" aria-label="Supplies and reminders">
          <SupplyListCard version={version} />
          {nextWash && (
            <Card className="stack-sm" as="div">
              <p className="kicker">Handwashing</p>
              <p className="row"><Icon name="hands" /><span>Next reminder at <strong>{prettyTime(nextWash)}</strong>.</span></p>
              <p className="caption">Staff get a nudge each hour during service and after raw meat.</p>
            </Card>
          )}
          {/* The real "Hand off the shift" button is in the close view (last hour of the day); before then, a preview. */}
          {moment !== "close" && <Link to="?moment=close" className="caption">Preview the shift hand-off</Link>}
          {t.me.checkin == null && s.employeeId && moment !== "evening" && !t.findings.some((f) => f.kind === "not_checked_in" && f.ref === s.employeeId) && <Notice tone="warn" title="Your check-in is still needed" action={<LinkButton to="/check-in" small variant="secondary" icon="hands">Start my check-in</LinkButton>}>It takes 30 seconds, and it's the one thing that must happen before food work.</Notice>}
          <p className="caption">Logging level: {s.location.logLevel ?? "essentials"}. <Link to="/settings#level">Change</Link> · {isoToLocalTime(new Date().toISOString(), tz)}</p>
        </aside>
      </div>
    </Page>
  );
}

function FindingGroup({ sev, items, onShift }: { sev: Finding["severity"]; items: Finding[]; onShift: Employee[] }) {
  return (
    <div>
      <div className="group-label"><Pill tone={sev === "urgent" ? "stop" : sev === "high" ? "warn" : "info"} icon={sev === "urgent" ? "alert" : sev === "high" ? "clock" : "info"}>{SEVERITY_GROUP[sev]}</Pill><span className="caption">{items.length}</span></div>
      <ul className="findings">{items.map((f, i) => <FindingCard key={`${f.kind}-${f.ref ?? i}-${i}`} f={f} onShift={onShift} />)}</ul>
    </div>
  );
}

/** The status band: "Open for food: Yes / Not yet", with the missing opening checks as check rows. */
function OpenForFood({ t, primary, onChange }: { t: TodayResponse; primary: boolean; onChange: () => void }) {
  const ready = t.openForFood.ready;
  const missing = t.expected.filter((e) => t.openForFood.missing.some((m) => m.key === e.key));
  // The day the place was set up isn't a full day: the opening checks are where you start, not a red "Not yet".
  const starting = !ready && missing.length > 0 && missing.every((e) => e.firstDay);
  return (
    <section className={`band ${ready ? "band-go" : starting ? "band-start" : "band-stop"}`} aria-labelledby="open-h">
      <div className="band-head">
        <h2 id="open-h"><Icon name={ready ? "check" : starting ? "sun" : "x"} size={28} /><span>{starting ? "Your first opening checks" : `Open for food: ${ready ? "Yes" : "Not yet"}`}</span></h2>
      </div>
      {ready ? <p>All opening checks are done.</p> : (
        <>
          <p>{starting ? "Do these before you serve food. Start with the first one:" : missing.length === 1 ? "One opening check left:" : `Finish these ${missing.length} opening checks first:`}</p>
          <ul className="checks">{missing.map((e, i) => <CheckRow key={e.key} e={e} onDone={onChange} primary={primary && i === 0} />)}</ul>
        </>
      )}
    </section>
  );
}

function OnShift({ t, onChange }: { t: TodayResponse; onChange?: () => void | Promise<void> }) {
  const s = useSession();
  const excludedAny = t.onShift.some((o) => shiftStatus(o.gate).excluded);
  return (
    <Section title="On shift" kicker={`${t.onShift.length} here today`}>
      {t.onShift.length === 0 ? <p className="muted">No one's on the schedule or checked in yet.</p> : (
        <ul className="people">
          {t.onShift.map(({ employee: e, gate }) => {
            const st = shiftStatus(gate);
            const first = e.name.split(" ")[0];
            return (
              <li key={e.id} className="person-row">
                <span className="avatar" aria-hidden="true">{e.name.slice(0, 1)}</span>
                <span className="grow">
                  <span className="person-name">{e.name}</span>{e.isPersonInCharge && <span className="caption"> · person in charge</span>}
                  {st.excluded && <span className="check-hint" style={{ display: "block" }}>{gate.blocking[0]?.detail}</span>}
                </span>
                <Pill tone={st.tone} icon={st.icon}>{st.label}</Pill>
                <PutInCharge e={e} onDone={onChange} />
                {e.phone && e.id !== s.employeeId && <AnchorButton small variant="secondary" icon="phone" href={`tel:${e.phone.replace(/[^\d+]/g, "")}`}>{`Call ${first}`}</AnchorButton>}
              </li>
            );
          })}
        </ul>
      )}
      {excludedAny && <p className="small"><Link to={sickWorkerGuide(placeState(s.location, s.org))}>Exclude or restrict? What the Food Code says</Link></p>}
    </Section>
  );
}

/** North star (FRAMEWORK T12): controlled shifts this week; checks completed today underneath as the leading indicator. */
function NorthStar({ t, version, doneCount }: { t: TodayResponse; version: number; doneCount: number }) {
  const api = useApi();
  const s = useSession();
  const from = addDays(t.date, -6);
  const week = useLoad(async () => {
    const [entries, checkins, people] = await Promise.all([api.entries(s.org.id, { locationId: s.location.id, from, to: t.date }), api.checkins(s.org.id, { locationId: s.location.id, from, to: t.date }), api.employees(s.org.id)]);
    return { entries, verdicts: controlledShifts(entries, checkins, people) };
  }, [api, s.org.id, s.location.id, from, t.date, version]);
  const v = week.data?.verdicts ?? [];
  const controlled = v.filter((x) => x.controlled).length;
  const caught = week.data ? caughtEarly(week.data.entries) : 0;
  return (
    <Section title="This week" kicker="How it's going">
      <Card className="star">
        {!week.data ? (week.error ? <ErrorBox error={week.error} onRetry={week.reload} /> : <Skeleton rows={1} />) : (
          <>
            <Progress value={controlled} max={v.length} label="shifts controlled" sub={caught > 0 ? <Pill tone="go" icon="check">Caught early {caught}</Pill> : undefined} />
            <p className="caption">A controlled shift: checked in before the first food task, and every reading in range or fixed.</p>
            {v.filter((x) => !x.controlled).slice(-2).map((x) => <p key={`${x.employeeId}${x.date}`} className="small muted">{new Date(x.date + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" })}: {x.reason}.</p>)}
          </>
        )}
        <div className="star-sub">
          <p className="kicker">Checks today</p>
          <p className="small"><strong className="num">{doneCount} of {t.expected.length}</strong> checks logged so far.</p>
        </div>
      </Card>
    </Section>
  );
}

/** Owner's evening answer (PEOPLE_AND_NEGATION: one line, or the one thing that isn't). */
function EveningLine({ t, version }: { t: TodayResponse; version: number }) {
  const api = useApi();
  const s = useSession();
  const day = useLoad(async () => {
    const [entries, checkins, people] = await Promise.all([api.entries(s.org.id, { locationId: s.location.id, from: t.date, to: t.date }), api.checkins(s.org.id, { locationId: s.location.id, from: t.date, to: t.date }), api.employees(s.org.id)]);
    return controlledShifts(entries, checkins, people);
  }, [api, s.org.id, s.location.id, t.date, version]);
  const notOk = (day.data ?? []).filter((x) => !x.controlled);
  const openRequired = t.findings.filter((f) => f.severity === "urgent");
  const line = !day.data ? "…" : notOk.length === 0 && openRequired.length === 0
    ? "Today: every shift controlled."
    // The thing, not the person (PEOPLE_AND_NEGATION: no blame-shaped wording).
    : openRequired[0] ? `Today: one thing needs you. ${findingTitle(openRequired[0]).replace(/^[^:]+: check-in still needed$/, "A check-in is still needed")}.` : `Today: one thing needs you. ${notOk[0]!.reason}.`;
  return (
    <Card className="star">
      <p className="kicker">Tonight</p>
      <p className="star-line">{line}</p>
      {notOk.length + openRequired.length > 1 && <p className="caption">{notOk.length + openRequired.length - 1} more below.</p>}
    </Card>
  );
}

/** Close: what's still open carries to the next shift (ROLE_ONTOLOGY: hand off the shift). */
function Handoff({ t, onShift, reload }: { t: TodayResponse; onShift: Employee[]; reload?: () => void | Promise<void> }) {
  const s = useSession();
  const nowIso = new Date().toISOString();
  const pending = t.expected.filter((e) => !e.satisfied && e.dueAt <= new Date(Date.now() + 2 * 3600_000).toISOString());
  const rest = t.findings.filter((f) => f.kind !== "missed_check");
  const ranked = [...pending].sort((a, b) => Number(!!b.required) - Number(!!a.required) || Number(isLate(b, nowIso)) - Number(isLate(a, nowIso)));
  const important = ranked.filter((e) => e.required || e.blocking);
  const first = important.length ? important.slice(0, 6) : ranked.slice(0, 4);
  const more = ranked.filter((e) => !first.includes(e));
  return (
    <Section title="Still open" kicker="Shift handoff">
      {/* Last hour of the day: a real button that puts the hand-off on the next person's list (core handoff.ts). */}
      <HandoffShift t={t} onDone={reload} />
      {pending.length === 0 && rest.length === 0 ? <Empty wink>Nothing to hand off. A clean close.</Empty> : (
        <>
          {/* Required and late first; routine and periodic tasks fold away so the handoff is never a wall (PEOPLE_AND_NEGATION). */}
          {first.length > 0 && <ul className="checks">{first.map((e) => <CheckRow key={e.key} e={e as Expected} showWhy={false} />)}</ul>}
          {more.length > 0 && (
            <details className="card card-flat">
              <summary className="disclose">{more.length} more routine {more.length === 1 ? "task" : "tasks"}</summary>
              <ul className="checks" style={{ marginTop: 12 }}>{more.map((e) => <CheckRow key={e.key} e={e as Expected} showWhy={false} />)}</ul>
            </details>
          )}
          {rest.length > 0 && <ul className="findings">{rest.map((f, i) => <FindingCard key={`${f.kind}-${f.ref ?? i}`} f={f} onShift={onShift} />)}</ul>}
          <p className="caption">{pending.filter((e) => isLate(e, nowIso)).length} checks were due earlier. The next person in charge sees this same list {nextOpenLine(s.location)}.</p>
        </>
      )}
    </Section>
  );
}

/* ─────────────── staff ─────────────── */

/** "at 7:00 AM" tomorrow, or "on Monday at 7:00 AM" after a closed day (core nextOpening, weekly hours). */
function nextOpenLine(loc: Location): string {
  const n = nextOpening(loc, new Date(), loc.timezone || "America/Denver");
  if (!n) return "at opening";
  return n.tomorrow || localDate(new Date(), loc.timezone) === n.date ? `at ${prettyTime(n.open)}` : `on ${DAY_NAMES[n.day]} at ${prettyTime(n.open)}`;
}

function StaffToday({ t, moment, sync, nowHHMM, version }: { t: TodayResponse; moment: Moment; sync: SyncStatus; nowHHMM: string; version: number }) {
  const s = useSession();
  const first = firstName(s.me.user.name);
  const blocking = t.me.gate.blocking[0];
  const action = t.me.checkin?.health ? checkinAction(t.me.checkin.health) : undefined;
  const excluded = !!t.me.checkin && !t.me.checkin.okToWork;
  // The person in charge's decision holds whatever a later check-in says (Food Code 2-201.13, fix S3).
  const decided = t.me.gate.blocking.find((b) => b.title === HEALTH_DECISION_BLOCK_TITLE);
  const rule = action ? returnRulesFor(placeState(s.location, s.org))[action] : undefined;

  return (
    <Page title={<span className="greet">{greeting(moment, nowHHMM)}, {first}</span>} kicker={<>{new Date(t.date + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })} · {s.location.name}</>} actions={<SyncNote sync={sync} />} narrow>
      <div className="stack">
        {/* The person in charge (a shift lead) gets a teammate's "Get help" here too (core helprequest.ts). */}
        <NeedsHelp version={version} />
        {/* 1. The gate: the one thing that must happen before food work. */}
        {decided ? (
          <Blocked title="No food work today">
            <p style={{ color: "var(--ink)" }}>{workerGateLine(decided)}</p>
          </Blocked>
        ) : !t.me.checkin ? (
          <Blocked title="Check in before you touch food" action={<LinkButton to="/check-in" big icon="hands">Start my check-in</LinkButton>}>
            <p className="muted" style={{ color: "var(--ink)" }}>A few quick health questions, then wash your hands. About 30 seconds.</p>
          </Blocked>
        ) : excluded ? (
          <Blocked title="No food work today">
            <p style={{ color: "var(--ink)" }}>{action ? checkinActionsFor(placeState(s.location, s.org))[action].worker : "Your check-in says no food work today."}</p>
            {/* When they may come back (Food Code 2-201.13, core returnRulesFor(placeState(s.location, s.org))), fix S3. */}
            {rule && action !== "cover" && <p className="small"><strong>When can I come back?</strong> {rule.worker} <span className="caption">({rule.citation})</span></p>}
            <p><Link to="/check-in">Answer again</Link> if something changed.</p>
          </Blocked>
        ) : blocking ? (
          <Blocked title="Finish your lessons first" action={<LinkButton to="/learn" big icon="learn">Finish my lessons</LinkButton>}>
            <p style={{ color: "var(--ink)" }}>{blocking.detail}</p>
          </Blocked>
        ) : (
          <Notice tone="go" title="You're good to work">
            {action === "caution" ? checkinActionsFor(placeState(s.location, s.org)).caution.worker : `Checked in at ${isoToLocalTime(t.me.checkin.at, s.location.timezone)}. Wash again after the restroom, raw meat, trash, or your phone.`}
          </Notice>
        )}

        {/* 2. Reminders from the manager. */}
        {(t.me.reminders ?? []).map((r, i) => <Notice key={i} tone="info" icon="bell" title={`From your manager · ${isoToLocalTime(r.at, s.location.timezone)}`}>{r.message}</Notice>)}

        {/* 2b. Required signs not yet marked posted: one quiet card for managers, never a gate. */}
        {t.me.gate.ready && <Suspense fallback={null}><SignsNudge /></Suspense>}

        {/* 3. The routine: one mixed list (checks, cleaning, maintenance, reports, stock), each with How and Buy. */}
        {t.me.gate.ready && <RoomsLine version={version} />}
        {t.me.gate.ready && <CleanPlaceLine />}
        {t.me.gate.ready && <RoomLogs t={t} />}
        <PhotosToAdd t={t} />
        {t.me.gate.ready && <Routine expected={t.expected} version={version} />}

        {/* 4. The three verbs. */}
        <div className="verbs" role="navigation" aria-label="Checks, Report, Paper logs">
          <Link to="/do" className="verb"><Icon name="do" size={26} /><strong>Checks</strong><span>The checks due now</span></Link>
          <Link to="/report" className="verb"><Icon name="report" size={26} /><strong>Report</strong><span>A problem, with a photo</span></Link>
          <Link to="/paper" className="verb"><Icon name="camera" size={26} /><strong>Paper logs</strong><span>Print or snap a sheet</span></Link>
        </div>

        {t.findings.filter((f) => f.kind === "incident").length > 0 && (
          <Section title="Open reports" kicker="Is it still there?">
            <ul className="findings">{t.findings.filter((f) => f.kind === "incident").map((f, i) => <FindingCard key={i} f={f} />)}</ul>
          </Section>
        )}
      </div>
    </Page>
  );
}
